import React, { useEffect, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { SortableContainer } from 'react-sortable-hoc';
import TextField from '@material-ui/core/TextField';
import Milestone from '../../students/components/ScheduleMilestoneItem';
import useSnack from 'hooks/useSnack';
import arrayMove from 'array-move';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import Tooltip from '@material-ui/core/Tooltip';
import Fab from '@material-ui/core/Fab';
import SaveIcon from '@material-ui/icons/Save';
import { GET_ALL_MODULES } from 'graphql/admin/curriculum';
import { useQuery } from 'react-apollo';
import usePromise from 'hooks/usePromise';

const useStyles = makeStyles((theme) => ({
  root: {
    maxWidth: 700,
  },
  card: {
    marginBottom: theme.spacing(2),
  },
  cardFooter: {
    justifyContent: 'center',
  },
  footer: {
    textAlign: 'center',
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  fab: {
    position: 'fixed',
    bottom: 30,
    right: 30,
  },
  addContainer: {
    marginTop: theme.spacing(3),
  },
}));

const SortableMilestones = SortableContainer(
  ({ items, onAddMilestone, canEdit, ...other }) => {
    return (
      <div>
        {items.map((m, i) => {
          return (
            <>
              <Milestone
                key={i}
                index={i}
                milestone={m}
                milestones={items}
                canEdit={canEdit}
                {...other}
              />
              {canEdit && (
                <Box mb={2} textAlign="center">
                  <Button
                    type="button"
                    variant="outlined"
                    startIcon={<AddCircleOutlineIcon />}
                    color="primary"
                    onClick={() => onAddMilestone(i + 1)}
                  >
                    Add Milestone
                  </Button>
                </Box>
              )}
            </>
          );
        })}
      </div>
    );
  },
);

const TemplateBuilder = ({ data, history, onSave, canEdit }) => {
  const classes = useStyles();
  const snack = useSnack();
  const [name, setName] = useState(data?.name);
  const [milestones, setMilestones] = useState([]);
  const removedMilestones = useRef([]);
  const removedTasks = useRef([]);
  const modulesResp = useQuery(GET_ALL_MODULES, {
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (data && modulesResp.data) {
      setMilestones(data.milestones);
    }
  }, [modulesResp]);

  const onAddMilestone = (atIndex) => {
    console.log(atIndex);
    milestones.splice(atIndex, 0, {
      name: '',
      exercises: [],
    });
    setMilestones([...milestones]);
  };

  const onAddExercise = (milestone, module, exercise) => {
    milestone.exercises.push({ module, exercise });
    setMilestones([...milestones]);
  };

  const onRemoveExercise = (milestone, ex) => {
    milestone.exercises = milestone.exercises.filter((e) => e !== ex);
    setMilestones([...milestones]);
    removedTasks.current.push(ex);
  };

  const onRemoveMilestone = (milestone) => {
    setMilestones(milestones.filter((m) => m !== milestone));
    removedMilestones.current.push(milestone);
  };

  const onUpdateMilestone = (milestone) => {
    setMilestones(milestones.map((m) => (m === milestone ? milestone : m)));
  };

  const [saveHandler, saving] = usePromise(async () => {
    try {
      if (!name) {
        throw new Error('Please enter template name');
      }
      const upsertMilestones = milestones.map((m, order) => {
        if (!m.name) {
          throw new Error('Please enter milestone name');
        }
        return {
          name: m.name,
          order,
          exercises: m.exercises.map((i, idx) => ({
            module: i.module,
            exercise: i.exercise,
            order: idx,
          })),
        };
      });
      await onSave({
        name,
        milestones: upsertMilestones,
      });
      snack.success('Data saved.');
      history.push(`/timeline-templates/`);
    } catch (e) {
      snack.error(e.message);
    }
  });

  const onMove = () => {
    setMilestones([...milestones]);
  };

  const onSortEnd = ({ oldIndex, newIndex }) => {
    if (oldIndex === newIndex) return;
    const old = milestones[oldIndex];
    const _new = milestones[newIndex];
    old.order = newIndex;
    _new.order = oldIndex;
    setMilestones([...arrayMove(milestones, oldIndex, newIndex)]);
  };

  return (
    <div className={classes.root}>
      <React.Fragment>
        <TextField
          disabled={!canEdit}
          fullWidth
          margin="dense"
          label="Template name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={{ marginBottom: 16 }}
        />
        <SortableMilestones
          canEdit={canEdit}
          items={milestones}
          onMove={onMove}
          modules={modulesResp.data ? modulesResp.data.modules : []}
          onRemoveExercise={onRemoveExercise}
          onRemoveMilestone={onRemoveMilestone}
          onAddExercise={onAddExercise}
          onUpdateMilestone={onUpdateMilestone}
          classes={classes}
          onSortEnd={onSortEnd}
          useDragHandle={true}
          onAddMilestone={onAddMilestone}
          lockAxis="y"
        />
        {canEdit && (
          <div className={classes.fab}>
            <Tooltip title="Save" placement="left">
              <Fab
                size="large"
                color="primary"
                onClick={saveHandler}
                disabled={saving}
              >
                <SaveIcon />
              </Fab>
            </Tooltip>
          </div>
        )}
      </React.Fragment>
    </div>
  );
};

export default SortableContainer(TemplateBuilder);
